import { find, size } from 'lodash-es';
import axios from 'axios';
import PromiseStore from 'Assets/js/components/promiseStore';
import rateLimit from 'axios-rate-limit';
import {initIndexedDBStorage, getProductsByIdsIndexed, getProductByIdIndexed, getPriceByProductId, getProductByVariantIdIndexed, addProducts, replaceProducts, getAllDBProducts, setPrices, getPrices} from "./indexDBStorage.js"
import Fuse from 'fuse.js'

// Global theme wrapper
const globalTheme = (() => {
  const http = rateLimit(axios.create(), {
    maxRequests: 6,
    perMilliseconds: 1000,
    maxRPS: 6
  });
  const globalThemeObject = window.global_theme_object || {};
  let fetchProductsPromise = null

  let ensuringData = null

  const ensureData = async () => {
    if (ensuringData) return ensuringData
    ensuringData = (async () => {
      await ready
      const all = await getAllDBProducts()
      if (!Array.isArray(all) || all.length === 0) await seedIfEmpty()
    })().finally(() => { ensuringData = null })
    return ensuringData
  }

  const ready = initIndexedDBStorage('productStore', Number(globalThemeObject.product_version_hash) || 1)

  const pricesKey = () => `${globalThemeObject.user_local || ''}:${globalThemeObject.product_version_hash || '0'}`

  let searchInstance = null

  const getByPath = (obj, path) => {
    if (!obj || !path) return undefined
    return path.split('.').reduce((acc, key) => {
      if (acc && typeof acc === 'object' && key in acc) return acc[key]
      return undefined
    }, obj)
  }

  const isTruthyLike = value => {
    if (typeof value === 'boolean') return value
    const normalized = String(value ?? '').trim().toLowerCase()
    return ['1', 'true', 'yes', 'on'].includes(normalized)
  }

  const isHiddenProduct = product => {
    const meta = product?.meta || {}
    const shopifyMeta = product?.shopify_metafields || product?.product_shopify_meta || {}
    const candidates = [
      meta['custom.hidden_product'],
      meta.hidden_product,
      meta.custom_hidden_product,
      getByPath(meta, 'custom.hidden_product'),
      shopifyMeta['custom.hidden_product'],
      shopifyMeta.hidden_product,
      shopifyMeta.custom_hidden_product,
      getByPath(shopifyMeta, 'custom.hidden_product')
    ]
    return candidates.some(isTruthyLike)
  }

  const visibleProducts = list =>
    (Array.isArray(list) ? list : []).filter(item => !isHiddenProduct(item))

  const buildSearch = async () => {
    const all = await getAllDBProducts();
    searchInstance = new Fuse(all, {
      keys: [
        { name: 'title', weight: 0.7 },
        { name: 'description', weight: 0.3 },
        { name: 'handle', weight: 0.1 }
      ],
      threshold: 0.3,
      distance: 100,
      ignoreLocation: true,
      minMatchCharLength: 2,
      includeScore: true
    });

    return searchInstance;
  };

  const reinitForCurrentVersion = async () => {
    const g = window.global_theme_object || {};
    const v = Number(g.product_version_hash) || 1;
    const key = `ps:products:fetched:v${v}`;
    try { sessionStorage.removeItem(key); } catch (e) {}
    fetchProductsPromise = null;
    searchInstance = null;
    await initIndexedDBStorage('productStore', v);
    const list = await fetchProductsOnce({ forceRefresh: true });
    if (Array.isArray(list)) await replaceProducts(list);
    window.dispatchEvent(new Event('products-ready'));
  };

  const startVersionWatcher = (() => {
    let last = Number((window.global_theme_object || {}).product_version_hash) || 1;
    let started = false;
    return () => {
      if (started) return;
      started = true;
      const tick = async () => {
        const g = window.global_theme_object || {};
        const v = Number(g.product_version_hash) || 1;
        if (v !== last) {
          last = v;
          await reinitForCurrentVersion();
        }
        setTimeout(tick, 1000);
      };
      tick();
    };
  })();

  const fetchProductsOnce = async ({ forceRefresh = false } = {}) => {
    if (!forceRefresh && fetchProductsPromise) return fetchProductsPromise
    const v = Number((window.global_theme_object || {}).product_version_hash) || 1
    const key = `ps:products:fetched:v${v}`

    await ready
    if (!forceRefresh) {
      const existing = await getAllDBProducts()
      const hasData = Array.isArray(existing) && existing.length > 0
      if (sessionStorage.getItem(key) === '1' && hasData) return []
    }

    const fetchPromise = Promise.all([
      http.get('/api/shopify_products/'),
      http.get('/wp-json/oss/v1/product-prices')
    ]).then(([prodResp, priceResp]) => {
      const d = prodResp && prodResp.data
      const list = Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : []
      const priceMap = priceResp && priceResp.data && typeof priceResp.data === 'object' ? priceResp.data : {}
      const merged = list.map(p => {
        const prices = priceMap[p.id] || {}
        return { ...p, prices }
      })
      const filtered = visibleProducts(merged)
      if (filtered.length > 0) sessionStorage.setItem(key, '1')
      else sessionStorage.removeItem(key)
      return filtered
    }).catch(err => {
      if (fetchProductsPromise === fetchPromise) fetchProductsPromise = null
      throw err
    })

    fetchProductsPromise = fetchPromise
    return fetchPromise
  }


  const warmProducts = (() => {
    let started = false
    return async () => {
      if (started) return
      started = true
      await ready
      const existing = await getAllDBProducts()
      if (existing && existing.length) return
      fetchProductsOnce().then(async list => {
        if (list && list.length) {
          await addProducts(list)
          window.dispatchEvent(new Event('products-ready'))
        }
      }).catch(() => {})
    }
  })()

  if (document.readyState === 'complete') warmProducts()
  else window.addEventListener('load', warmProducts)

  const seedIfEmpty = async () => {
      await ready
      let productsDB = await getAllDBProducts()
      if (Array.isArray(productsDB) && productsDB.length) return productsDB
      const list = await fetchProductsOnce()
      if (list.length) await addProducts(list)
      productsDB = await getAllDBProducts()
      return productsDB || []
  }

  const refreshProductsData = async () => {
    try {
      await ready
      const list = await fetchProductsOnce({ forceRefresh: true })
      if (Array.isArray(list)) {
        await replaceProducts(list)
        window.dispatchEvent(new Event('products-ready'))
        return true
      }
    } catch (e) { /* noop */ }
    return false
  }

  ;(async () => {
    await startVersionWatcher();
    await seedIfEmpty()
    // Hydrate product prices via IndexedDB or endpoint, then cache
    try {
      await ready
      const current = globalThemeObject.product_prices || {}
      if (!current || Object.keys(current).length === 0) {
        const cached = await getPrices(pricesKey())
        if (cached && typeof cached === 'object') {
          globalThemeObject.product_prices = cached
          window.dispatchEvent(new CustomEvent('oss:product-prices-loaded'))
        } else if (globalThemeObject.product_prices_endpoint) {
          const resp = await http.get(globalThemeObject.product_prices_endpoint)
          const data = resp && resp.data ? resp.data : {}
          if (data && typeof data === 'object') {
            globalThemeObject.product_prices = data
            try { await setPrices(pricesKey(), data) } catch (e) {}
            window.dispatchEvent(new CustomEvent('oss:product-prices-loaded'))
          }
        }
      }
    } catch (e) { /* noop */ }
  })()

  const products = getProducts

  const getProducts = async () => {
    await ready
    await ensureData()
    const items = await getAllDBProducts()
    return visibleProducts(items)
  }

  const toGid = id => String(id).startsWith('gid://shopify/Product/') ? String(id) : `gid://shopify/Product/${id}`

  const productsByIds = async productIds => {
    await ready
    await ensureData()
    const gids = (Array.isArray(productIds) ? productIds : []).map(toGid)
    const expected = gids.length
    const load = async () => {
      const hits = await getProductsByIdsIndexed(gids)
      if (hits.length === gids.length) return hits
      const missing = new Set(gids.filter(g => !hits.find(h => h && h.id === g)))
      const list = await getProducts()
      const backfilled = list.filter(p => missing.has(p.id))
      const merged = [...hits, ...backfilled].filter(Boolean)
      const byId = new Map(merged.map(p => [p.id, p]))
      return gids.map(g => byId.get(g)).filter(Boolean)
    }
    let results = await load()
    if (results.length === expected || expected === 0) return results
    const refreshed = await refreshProductsData()
    if (!refreshed) return results
    results = await load()
    return results
  }

  const productsByFullIds = async productIds => {
    await ready
    await ensureData()
    const ids = Array.isArray(productIds) ? productIds : []
    const expected = ids.length
    const load = async () => {
      const hits = await getProductsByIdsIndexed(ids)
      if (hits.length === ids.length) return hits
      const missing = new Set(ids.filter(g => !hits.find(h => h && h.id === g)))
      const list = await getProducts()
      const backfilled = list.filter(p => missing.has(p.id))
      const merged = [...hits, ...backfilled].filter(Boolean)
      const byId = new Map(merged.map(p => [p.id, p]))
      return ids.map(g => byId.get(g)).filter(Boolean)
    }
    let results = await load()
    if (results.length === expected || expected === 0) return results
    const refreshed = await refreshProductsData()
    if (!refreshed) return results
    results = await load()
    return results
  }

  const getPrice = async id => {
    await ready
    const hit = await getPriceByProductId(id)
    if (hit) return hit
    return {}
  }

  const getPriceWithVAT = async (id) => {
    await ready
    await ensureData()
    const waitForVat = () =>
      new Promise(resolve => {
        const started = Date.now()
        const tick = () => {
          const g = window.global_theme_object || {}
          const vat = g.vat_by_country || {}
          if (Object.keys(vat).length || Date.now() - started > 2000) resolve(vat)
          else setTimeout(tick, 50)
        }
        tick()
      })

    const g = window.global_theme_object || {}
    let vatMap = g.vat_by_country || {}
    if (!Object.keys(vatMap).length) vatMap = await waitForVat()

    const hit = await getPriceByProductId(id)
    if (hit) {
      const current = hit[localCode()];
      const VATPercent = vatMap[localCode()];
      const priceWithVat = current * (1 + VATPercent)

      return formatPrice(priceWithVat);
    }
    return {}
  };

  const getVatLocalVatRate = async (id) => {
    await ready
    await ensureData()
    const waitForVat = () =>
      new Promise(resolve => {
        const started = Date.now()
        const tick = () => {
          const g = window.global_theme_object || {}
          const vat = g.vat_by_country || {}
          if (Object.keys(vat).length || Date.now() - started > 2000) resolve(vat)
          else setTimeout(tick, 50)
        }
        tick()
      })

    const g = window.global_theme_object || {}
    let vatMap = g.vat_by_country || {}
    if (!Object.keys(vatMap).length) vatMap = await waitForVat()

    const hit = await getPriceByProductId(id)
    if (hit) {
      const current = hit[localCode()];
      return vatMap[localCode()];
    }
    return 0;
  };

  const searchProducts = async (q) => {
    await ready;
    if (!searchInstance) await buildSearch();
    if (!q) return getAllDBProducts();
    const res = searchInstance.search(q);

    res.sort((a, b) => {
      const weightA = parseInt(a.item.meta.search_weight || 1, 10);
      const weightB = parseInt(b.item.meta.search_weight || 1, 10);

      a.calculatedScore = (1 - a.score) * (a.item.prices.IE || 1) * weightA;
      b.calculatedScore = (1 - b.score) * (b.item.prices.IE || 1) * weightB;
      return b.calculatedScore - a.calculatedScore;
    });

    return res.map((r) => r.item);
  };

  const productByVariantId = async id => {
    await ready
    await ensureData()
    const locate = async () => {
      const hit = await getProductByVariantIdIndexed(id)
      if (hit && hit.id) return hit
      const list = await getProducts()
      return find(list, p => Array.isArray(p?.variants) && find(p.variants, { id })) || null
    }
    let product = await locate()
    if (product && product.id) return product
    const refreshed = await refreshProductsData()
    if (!refreshed) return product || {}
    product = await locate()
    return product || {}
  }

  const productById = async productId => {
    await ready
    await ensureData()
    const gid = `gid://shopify/Product/${productId}`
    const locate = async () => {
      const hit = await getProductByIdIndexed(gid)
      if (hit && hit.id) return hit
      const list = await getProducts()
      return find(list, { id: gid }) || null
    }
    let product = await locate()
    if (product && product.id) return product
    const refreshed = await refreshProductsData()
    if (!refreshed) return product || {}
    product = await locate()
    return product || {}
  }

  const productByFullId = async productId => {
    await ready
    await ensureData()
    const locate = async () => {
      const hit = await getProductByIdIndexed(productId)
      if (hit && hit.id) return hit
      const list = await getProducts()
      return find(list, { id: productId }) || null
    }
    let product = await locate()
    if (product && product.id) return product
    const refreshed = await refreshProductsData()
    if (!refreshed) return product || {}
    product = await locate()
    return product || {}
  }

  const varientIdByProductId = async productId => {
    const p = await productByFullId(productId)
    return p.variants && p.variants[0] && p.variants[0].id
  }

  const randomProduct = async () => {
    const list = await getProducts()
    return list[Math.floor(Math.random() * size(list))]
  }

  const randomProducts = async count => {
    const list = await getProducts()
    if (!Array.isArray(list) || list.length === 0) return []
    const shuffled = [...list].sort(() => 0.5 - Math.random())
    return shuffled.slice(0, Math.min(count, list.length))
  }

  //****************************^ product stuff ^*****************************//

  const currentBrand = () => globalThemeObject.current_brand || 'mdc';

  const brands = () => globalThemeObject.brands || {};

  const cartId = () => globalThemeObject.user_cart_id || '';

  const promotions = () => globalThemeObject.promotions || {};

  const promotionDetails = () => globalThemeObject.promotiondetails || {};

  const currentMarketCode = () => {
    // eslint-disable-next-line no-use-before-define
    const currentCurrency = currency();
    return currentCurrency.country_code
      ? currentCurrency.country_code.replace(/_/g, '-')
      : 'en-IE';
  };

  const currentMarket = () => globalThemeObject.user_local;

  const einsteinRecs = () => globalThemeObject.einstein_recs;
  const einsteinCart = () => globalThemeObject.einstein_cart;

  const setCurrentMarket = (marketLocale) => {
    // Align global theme object
    globalThemeObject.user_local = marketLocale;
    // Align cookie
    document.cookie = `STYXKEY_locale=${marketLocale}; expires=Session; path=/;`;
  };

  const country = () =>
    (globalThemeObject.markets.markets[globalThemeObject.user_local] || {})
      .name;

  const language = () =>
    (globalThemeObject.markets.markets[globalThemeObject.user_local] || {})
      .language;

  const currency = () =>
    (globalThemeObject.markets.markets[globalThemeObject.user_local] || {})
      .currency;

  const geoLocation = async () => {
    if (sessionStorage.getItem('geoLocation')) {
      return;
    }

    const config = {
      method: 'get',
      url: `${globalThemeObject.geo_path}`,
      headers: {
        'Content-Type': 'application/json'
      }
    };

    await http(config).then((response) => {
      sessionStorage.setItem('geoLocation', response.data.country);
      sessionStorage.setItem('geoLocationMethod', response.data.method);
    });
  };

  const env = (varName) => globalThemeObject[varName] || null;

  const ecrmMid = () => globalThemeObject.ecrm_mid || '';

  /**
   * @returns {[]|*}
   */
  const featureEnabled = () => globalThemeObject.feature_enabled || {};

  const formatTitle = (title) => title && title.split('||')[0];

  const getFormatter = () =>
    // @todo Number format should be driven by market!
    new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      roundingMode: 'halfEven' // Important: it handles correctly rounding of e.g. 1099.005
    });

  const formatPrice = (price, withCurrency = false) => {
    const formatter = getFormatter();
    // eslint-disable-next-line no-shadow
    const { currency } = globalThemeObject;

    // Convert to float
    if (typeof price === 'string') {
      price = parseFloat(price);
    }

    // Format price
    let formattedPrice = formatter.format(price);

    // Get formatted price, excluding currency prefix
    const match = formattedPrice.match(/([\d.,]*)$/gi);
    if (match) {
      // eslint-disable-next-line prefer-destructuring
      formattedPrice = match[0];
    }

    // IMPORTANT: This must be always aligned with Core/Locale/GetProductPriceAbstract.php, method get_price()
    // Return price: currency (if different from currency symbol) + currency symbol + formatted price
    const prefix =
      (withCurrency && currency.currency !== currency.currency_symbol
        ? `<small>${currency.currency}</small> `
        : '') + currency.currency_symbol;

    return `${prefix} ${formattedPrice}`;
  };

  const hasPrice = (productId) => !!globalThemeObject.product_prices[productId];

  const localCode = () =>
    (globalThemeObject?.markets?.markets[globalThemeObject.user_local] || {})
      .country_code;

  const marketExclusive = () => globalThemeObject.market_exclusive || false;

  const markets = () =>
    (window.global_theme_object || globalThemeObject).markets?.markets || {};

  const notifyMeBlockEnabled = () =>
    globalThemeObject.notify_me_block_enabled === 'true';

  const price = (productId) => {
    const vat = globalThemeObject.vat_by_country || {};
    const cc = (
      globalThemeObject.markets.markets[globalThemeObject.user_local] || {}
    ).country_code;
    const vatAmount = vat[cc];

    const prices = globalThemeObject.product_prices[productId];
    return (prices && prices[cc] + prices[cc] * vatAmount) || 0;
  };

  const priceWithoutVat = (productId) => {
    const cc = (
      globalThemeObject.markets.markets[globalThemeObject.user_local] || {}
    ).country_code;

    const prices = globalThemeObject.product_prices[productId];
    return (prices && prices[cc]) || 0;
  };

  const priceByCountry = (productId, countryId) => {
    countryId = countryId.toUpperCase();
    const cc = countryId;
    const prices = globalThemeObject.product_prices[productId];

    return prices && prices[cc];
  };

  // eslint-disable-next-line no-shadow
  const doVat = (price) => {
    const vat = globalThemeObject.vat_by_country || {};
    const cc = (
      globalThemeObject.markets.markets[globalThemeObject.user_local] || {}
    ).country_code;
    const vatAmount = vat[cc];

    const thePrice = parseFloat(price);

    return thePrice + thePrice * vatAmount;
  };

  const prices = () => globalThemeObject.product_prices;

  const getLocalRecommendations = async () => {
    const data = JSON.stringify({
      query: `query recommendations{
        recommendations
      }`
    });

    const config = {
      method: 'post',
      url: `${globalThemeObject.site_url}/graphql`,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    const things = await http(config).then(
      (response) => response.data.data?.recommendations
    );
    return things;
  };

  const cartSuggestions = async () => {
    const data = JSON.stringify({
      query: `query cartrecommendations{
        cartrecommendations
      }`
    });

    const config = {
      method: 'post',
      url: `${globalThemeObject.site_url}/graphql`,
      headers: {
        'Content-Type': 'application/json'
      },
      data
    };

    const things = await http(config).then(
      (response) => response.data.data.cartrecommendations
    );
    return things;
  };

  const search = () => globalThemeObject.search || {};

  const shopify = () => globalThemeObject.shopify || {};

  const shopifyBackInStock = () =>
    globalThemeObject.shopify_back_in_stock || '#';

  const siteUrl = () => globalThemeObject.site_url || '';

  const cdb = () => globalThemeObject.cdb || {};

  const userArea = () => globalThemeObject.user_area || {};

  function getCurrencyByCountryCode(countryCode) {
    // eslint-disable-next-line no-shadow
    const { markets } = globalThemeObject.markets || {};

    // eslint-disable-next-line no-restricted-syntax
    for (const key in markets) {
      // eslint-disable-next-line no-prototype-builtins
      if (markets.hasOwnProperty(key)) {
        const entry = markets[key];
        if (entry.country_code === countryCode.toUpperCase()) {
          return entry.currency;
        }
      }
    }

    return 'EUR';
  }

  const formatWithMarketCode = (sentPrice, marketCode) => {
    const currencyCode = getCurrencyByCountryCode(marketCode);
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currencyCode
    }).format(sentPrice);
  };

  // @todo This must come from back-end, e.g. window.global_theme_object.markets.markets
  /**
   * @param {string} countryCode
   * @returns {string}
   */
  const getCurrencySymbol = (countryCode) => {
    switch (countryCode) {
      case 'au':
        return '$';
      case 'ch':
        return 'CHF';
      case 'cn':
        return '¥';
      case 'de':
        return '€';
      case 'gb':
        return '£';
      case 'ie':
        return '€';
      case 'jp':
        return '¥';
      case 'nz':
        return '$';
      case 'sg':
        return 'S$';
      case 'fr':
        return '€';
      case 'za':
        return 'R';
      default:
        return '€';
    }
  };

  /**
   * @param {string} countryCode
   * @returns {*|string}
   */
  const getCurrencyByMarketCountryCode = (countryCode) => {
    countryCode = countryCode?.toUpperCase();
    const foundMarket = find(
      globalThemeObject.markets.markets || {},
      (market) => market.country_code === countryCode
    );

    return foundMarket?.currency || 'EUR';
  };

  const consumerResearchSurvey = () =>
    globalThemeObject.consumer_research_survey || {};

  const maintenanceMode = () => globalThemeObject.maintenance_mode || false;

  const isLocal = () => !!globalThemeObject.is_local;

  const emergency = () => globalThemeObject.emergency || {};

  const gtm = () => globalThemeObject.gtm || {};

  const b2bCorporatePage = () => globalThemeObject.b2b_corporate_page || '';

  const b2bInvoice = () => globalThemeObject.b2b_invoice || false;

  const cdbCountries = () => globalThemeObject.cdb_countries || [];

  const cartEngraveButtonWithPrice = () =>
    globalThemeObject.cart_engrave_button_with_price || false;

  const cartEngraveButtonCustomisation = () =>
    globalThemeObject.cart_engrave_button_customisation || false;

  const freeEngraveButtonCustomisation = () =>
    globalThemeObject.free_engrave_button_customisation || false;

  return {
    currentBrand,
    b2bCorporatePage,
    b2bInvoice,
    brands,
    doVat,
    cartId,
    cartSuggestions,
    einsteinRecs,
    einsteinCart,
    cdb,
    cdbCountries,
    consumerResearchSurvey,
    currency,
    freeEngraveButtonCustomisation,
    ecrm_mid: ecrmMid,
    geoLocation,
    currentMarketCode,
    currentMarket,
    env,
    featureEnabled,
    formatPrice,
    formatWithMarketCode,
    getCurrencySymbol,
    getCurrencyByMarketCountryCode,
    gtm,
    promotions,
    promotionDetails,
    formatTitle,
    getFormatter,
    hasPrice,
    localCode,
    maintenanceMode,
    marketExclusive,
    markets,
    notifyMeBlockEnabled,
    price,
    getPrice,
    getPriceWithVAT,
    getVatLocalVatRate,
    priceWithoutVat,
    prices,
    priceByCountry,
    productById,
    searchProducts,
    productsByIds,
    productsByFullIds,
    setCurrentMarket,
    country,
    language,
    productByFullId,
    varientIdByProductId,
    productByVariantId,
    randomProducts,
    products,
    search,
    shopify,
    shopifyBackInStock,
    siteUrl,
    getLocalRecommendations,
    randomProduct,
    userArea,
    isLocal,
    emergency,
    cartEngraveButtonWithPrice,
    cartEngraveButtonCustomisation
  };
})();
export default globalTheme;
